import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useContent } from '../hooks/useContent';

const defaultData = {
  title: 'Meeting / Training Room Rental Rates',
  image: 'https://images.pexels.com/photos/6646770/pexels-photo-6646770.jpeg',
  specification: '560 sqf',
  weekday_half_day: 'RM 700',
  weekday_full_day: 'RM 1,400',
  weekday_additional: 'RM 200',
  weekend_half_day: 'RM 800',
  weekend_full_day: 'RM 1,600',
  weekend_additional: 'RM 200',
  capacity_theatre: '50 pax',
  capacity_classroom: '44 pax',
  capacity_ushape: '21 pax',
  capacity_cluster: '34 pax',
  inclusions: ['Projector / mic system', 'Tables and chairs', 'Whiteboard', 'Registration table', 'Pantry access'],
  facilities: ['Event space setup based on approved layout', 'Air-conditioning within booking window policy', 'WiFi', 'Basic venue support'],
};

const RoomBooking = () => {
  const { data } = useContent('room-booking', {});
  const d = { ...defaultData, ...(data && data.id ? data : {}) };

  return (
    <section id="room-booking" className="py-20 bg-gradient-to-b from-[#FAF8F3] to-white" data-testid="room-booking-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20">EWRF HQ</Badge>
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A1628] mb-4">
            Room & Facility <span className="italic text-[#D4AF37]">Booking</span>
          </h2>
          <p className="text-gray-600">
            Book our meeting and training room at EWRF HQ for your events, workshops, and meetings.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            {d.image && (
              <img src={d.image} alt="Meeting Room" className="w-full h-64 object-cover rounded-lg mb-6" />
            )}
            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0A1628] mb-4">{d.title}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 text-[#0A1628]">Day Type</th>
                        <th className="text-left py-3 text-[#0A1628]">Half Day (4 hrs)</th>
                        <th className="text-left py-3 text-[#0A1628]">Full Day (8 hrs)</th>
                        <th className="text-left py-3 text-[#0A1628]">Additional Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 font-medium">Weekday</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekday_half_day}</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekday_full_day}</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekday_additional}</td>
                      </tr>
                      <tr>
                        <td className="py-3 font-medium">Weekend</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekend_half_day}</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekend_full_day}</td>
                        <td className="py-3 text-[#D4AF37] font-semibold">{d.weekend_additional}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <h4 className="font-semibold text-[#0A1628] mb-4">Room Specification — {d.specification}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2"><span className="text-[#D4AF37]">•</span><div><span className="font-medium">Theatre:</span> {d.capacity_theatre}</div></div>
                  <div className="flex items-start gap-2"><span className="text-[#D4AF37]">•</span><div><span className="font-medium">Classroom:</span> {d.capacity_classroom}</div></div>
                  <div className="flex items-start gap-2"><span className="text-[#D4AF37]">•</span><div><span className="font-medium">U Shape:</span> {d.capacity_ushape}</div></div>
                  <div className="flex items-start gap-2"><span className="text-[#D4AF37]">•</span><div><span className="font-medium">Cluster:</span> {d.capacity_cluster}</div></div>
                </div>
              </CardContent>
            </Card>

            {d.inclusions?.length > 0 && (
              <Card className="border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <h5 className="font-semibold text-[#0A1628] mb-3">Inclusions</h5>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {d.inclusions.map((item, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="text-[#D4AF37] mt-1">✓</span><span>{item}</span></li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {d.facilities?.length > 0 && (
              <Card className="border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <h5 className="font-semibold text-[#0A1628] mb-3">Facilities</h5>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {d.facilities.map((item, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="text-[#D4AF37] mt-1">✓</span><span>{item}</span></li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <button className="w-full bg-[#D4AF37] hover:bg-[#C19A2E] text-white py-3 rounded-lg font-semibold transition-colors">
              Enquire About Booking
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoomBooking;
